import { useState, useEffect, useCallback } from "react";
import type { VnProgram } from "../engine/Interpreter";

interface Diagnostic {
  severity: string;
  category: string;
  msg: string;
  source?: string;
  line?: number;
}

interface DiagnosticsPanelProps {
  program: VnProgram | null;
}

export function DiagnosticsPanel({ program }: DiagnosticsPanelProps) {
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchDiagnostics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/diagnostics");
      if (res.ok) {
        const data = await res.json();
        setDiagnostics(data.diagnostics ?? []);
        return;
      }
    } catch {
      // dev server not running — fall back to embedded diagnostics
    }

    // Fall back to diagnostics embedded in the loaded program
    if (program) {
      const embedded = (program as Record<string, unknown>).diagnostics;
      if (Array.isArray(embedded)) {
        setDiagnostics(embedded as Diagnostic[]);
      } else {
        setDiagnostics([]);
      }
    } else {
      setDiagnostics([]);
    }
    setLoading(false);
  }, [program]);

  useEffect(() => {
    fetchDiagnostics();
  }, [fetchDiagnostics]);

  const filtered = diagnostics.filter(
    (d) =>
      !filter ||
      d.msg.toLowerCase().includes(filter.toLowerCase()) ||
      d.category.toLowerCase().includes(filter.toLowerCase()) ||
      (d.source ?? "").toLowerCase().includes(filter.toLowerCase()),
  );

  const warnCount = diagnostics.filter((d) => d.severity === "warning").length;
  const infoCount = diagnostics.filter((d) => d.severity === "info").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0.5rem" }}>
      <div style={summaryStyle}>
        <span>
          {diagnostics.length === 0
            ? "No diagnostics"
            : `${warnCount} warning${warnCount !== 1 ? "s" : ""}, ${infoCount} info`}
        </span>
        <button onClick={fetchDiagnostics} style={refreshBtnStyle} disabled={loading}>
          {loading ? "..." : "refresh"}
        </button>
      </div>

      {diagnostics.length > 0 && (
        <input
          type="text"
          placeholder="Filter by message, category, or source..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={filterStyle}
        />
      )}

      <div style={listStyle}>
        {filtered.map((d, i) => (
          <div key={i} style={diagRowStyle}>
            <span style={severityStyle(d.severity)}>
              {d.severity === "warning" ? "WARN" : "INFO"}
            </span>
            <span style={categoryStyle}>{d.category}</span>
            <span style={msgStyle}>{d.msg}</span>
            {d.source && (
              <span style={sourceStyle}>
                {d.source}{d.line ? `:${d.line}` : ""}
              </span>
            )}
          </div>
        ))}
        {filtered.length === 0 && diagnostics.length > 0 && (
          <div style={{ opacity: 0.5, padding: "0.5rem" }}>No matches</div>
        )}
      </div>
    </div>
  );
}

function severityStyle(severity: string): React.CSSProperties {
  return {
    fontSize: "0.65rem",
    fontWeight: "bold",
    padding: "0.1rem 0.3rem",
    borderRadius: "2px",
    background: severity === "warning" ? "rgba(200, 160, 60, 0.2)" : "rgba(100, 160, 200, 0.2)",
    color: severity === "warning" ? "#c8a03c" : "#7ec8e3",
    whiteSpace: "nowrap",
  };
}

const summaryStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "0.4rem",
  fontSize: "0.75rem",
};

const refreshBtnStyle: React.CSSProperties = {
  background: "rgba(126, 200, 227, 0.1)",
  border: "1px solid rgba(126, 200, 227, 0.3)",
  color: "#7ec8e3",
  padding: "0.15rem 0.4rem",
  borderRadius: "3px",
  cursor: "pointer",
  fontSize: "0.65rem",
};

const filterStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.3rem 0.5rem",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "3px",
  color: "#e0e0e0",
  fontSize: "0.7rem",
  outline: "none",
  boxSizing: "border-box",
  marginBottom: "0.4rem",
};

const listStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
};

const diagRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.4rem",
  alignItems: "center",
  padding: "0.25rem 0",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  fontSize: "0.7rem",
  flexWrap: "wrap",
};

const categoryStyle: React.CSSProperties = {
  opacity: 0.6,
  fontSize: "0.65rem",
  whiteSpace: "nowrap",
};

const msgStyle: React.CSSProperties = {
  flex: 1,
  wordBreak: "break-word",
};

const sourceStyle: React.CSSProperties = {
  opacity: 0.4,
  fontSize: "0.6rem",
  whiteSpace: "nowrap",
};
